import { fireColor, writeFirePixelMuFast, writeWarmRangeColor } from './firePalette';

export const MAX_ITERATIONS = 255;
const WARM_RANGE_MAX_ITERATIONS = 1000;
const WARM_INTERIOR_32 = 0xff0e090a;
const ESCAPE_THRESHOLD = 4; // 2^2
const LOG2_INV = 1 / Math.log(2);
const NEBULABROT_MAX_SAMPLES = 1152000;
const NEBULABROT_QUANTILE_BINS = 1024;
const NEBULABROT_TONE_LUT_SIZE = 1024;

let nebulabrotToneLut = null;
let nebulabrotSqrtLut = null;
let nebulabrotBinsScratch = null;
let nebulabrotSmoothScratch = null;
let nebulabrotSmoothBuf = null;

function getNebulabrotLuts() {
  if (!nebulabrotToneLut) {
    nebulabrotToneLut = new Float32Array(NEBULABROT_TONE_LUT_SIZE + 1);
    nebulabrotSqrtLut = new Float32Array(NEBULABROT_TONE_LUT_SIZE + 1);
    for (let i = 0; i <= NEBULABROT_TONE_LUT_SIZE; i++) {
      const u = i / NEBULABROT_TONE_LUT_SIZE;
      nebulabrotSqrtLut[i] = Math.sqrt(u) * 0.42;
      nebulabrotToneLut[i] = Math.sqrt(u) * (u / (u + 0.012));
    }
  }
  return { toneLut: nebulabrotToneLut, sqrtLut: nebulabrotSqrtLut };
}

function getNebulabrotScratch(pixelCount) {
  if (!nebulabrotBinsScratch) {
    nebulabrotBinsScratch = new Uint32Array(NEBULABROT_QUANTILE_BINS);
  }
  if (!nebulabrotSmoothScratch || nebulabrotSmoothScratch.length < pixelCount) {
    nebulabrotSmoothScratch = new Float32Array(pixelCount);
  }
  if (!nebulabrotSmoothBuf || nebulabrotSmoothBuf.length < pixelCount) {
    nebulabrotSmoothBuf = new Float32Array(pixelCount);
  }
  return {
    binsScratch: nebulabrotBinsScratch,
    smoothScratch: nebulabrotSmoothScratch.subarray(0, pixelCount),
    smoothBuf: nebulabrotSmoothBuf.subarray(0, pixelCount),
  };
}

// Reusable result object to avoid allocations in hot loop
const iterResult = { iterations: 0, distanceSquared: 0 };

export function nebulabrotColor({ numIterations = 0 } = {}) {
  const v = Math.min(255, numIterations * 7);
  return [255, v, 0, Math.min(255, numIterations * 15)];
}

nebulabrotColor.colorParams = Object.freeze({
  palette: 'nebulabrot',
  mode: 'nebulabrot',
  useMu: false,
});

/**
 * Highly optimized iteration function
 * - Avoids array allocation by using reusable result object
 * - Uses manual abs() to avoid function call overhead
 * - Returns squared distance (caller computes sqrt only when needed)
 */
export function iterateUntilEscape(x0, y0, result, maxIterations = MAX_ITERATIONS) {
  if (x0 * x0 + y0 * y0 <= 0.0624995
    || (x0 >= -1.0189 && x0 <= -0.9811 && y0 >= -0.0189 && y0 <= 0.0189)) {
    result.iterations = maxIterations;
    result.distanceSquared = 0;
    return result;
  }
  let x = 0, y = 0;
  let iteration = 0;
  let x2 = 0, y2 = 0;
  let ox = NaN, oy = NaN;
  let checkAt = 8, step = 0;
  
  // Unrolled first iteration (we know x=0, y=0)
  // Skip first iteration's abs since x*y = 0
  
  while (x2 + y2 < ESCAPE_THRESHOLD && iteration < maxIterations) {
    const absXY = Math.abs(x * y);
    const y_n = absXY + absXY + y0; // 2 * absXY = absXY + absXY (avoids multiplication)
    x = x2 - y2 + x0;
    y = y_n;
    x2 = x * x;
    y2 = y * y;
    iteration++;
    if (x === ox && y === oy) {
      iteration = maxIterations;
      break;
    }
    if (++step === checkAt) {
      ox = x;
      oy = y;
      step = 0;
      checkAt <<= 1;
    }
  }
  
  result.iterations = iteration;
  result.distanceSquared = x2 + y2;
  return result;
}

/**
 * Re-normalized iteration count for smoother images
 * https://mathr.co.uk/helm/AtTheHelmOfTheBurningShip-Paper.pdf
 */
export function normalizedIterationCount(numIterations, distanceSquared) {
  // log(sqrt(x)) = 0.5 * log(x)
  const logDist = 0.5 * Math.log(distanceSquared);
  return numIterations + 1 - Math.log(logDist) * LOG2_INV;
}

export const getMu = normalizedIterationCount;

// Reusable object for colorFunc to avoid allocation in hot loop
const colorInput = { numIterations: 0, mu: 0 };

function renderShallowStrip(
  pixels, pixels32, canvasWidth, xBase, yBase, xScale, yScale,
  startX, endX, startY, endY, colorFunc, colorParams,
) {
  const result = iterResult;
  const cInput = colorInput;
  const range = colorParams?.valueRange ?? colorFunc?.colorParams?.valueRange;
  const hasValueRange = Array.isArray(range) && range.length === 2
    && Number.isFinite(range[0]) && Number.isFinite(range[1]) && range[0] < range[1];
  const maxIterations = hasValueRange ? WARM_RANGE_MAX_ITERATIONS : MAX_ITERATIONS;
  const useFastFire = !hasValueRange
    && colorFunc === fireColor
    && colorFunc?.colorParams?.palette === 'fire'
    && colorFunc?.colorParams?.useMu !== false;
  for (let y = startY; y < endY; y++) {
    const y0 = yBase + y * yScale;
    const rowOffset = y * canvasWidth;
    for (let x = startX; x < endX; x++) {
      const x0 = xBase + x * xScale;
      iterateUntilEscape(x0, y0, result, maxIterations);
      if (hasValueRange) {
        if (result.iterations === maxIterations) {
          pixels32[rowOffset + x] = WARM_INTERIOR_32;
        } else {
          const val = colorParams?.useMu === false
            ? result.iterations
            : normalizedIterationCount(result.iterations, result.distanceSquared);
          writeWarmRangeColor(pixels, (rowOffset + x) * 4, val, range);
        }
        continue;
      }
      if (result.iterations !== maxIterations) {
        if (useFastFire) {
          writeFirePixelMuFast(
            pixels, pixels32, rowOffset + x,
            result.iterations, result.distanceSquared, normalizedIterationCount,
          );
          continue;
        }
        cInput.numIterations = result.iterations;
        cInput.mu = normalizedIterationCount(result.iterations, result.distanceSquared);
        const [r, g, b, a] = colorFunc(cInput);
        const pr = r <= 0 ? 0 : r >= 255 ? 255 : r | 0;
        const pg = g <= 0 ? 0 : g >= 255 ? 255 : g | 0;
        const pb = b <= 0 ? 0 : b >= 255 ? 255 : b | 0;
        const pa = a <= 0 ? 0 : a >= 255 ? 255 : a | 0;
        pixels32[rowOffset + x] = pr | (pg << 8) | (pb << 16) | (pa << 24);
      }
    }
  }
}

/**
 * Choose the starting-orbit sample grid dimensions for a Nebulabrot render.
 * Supersamples 2x2 at inline resolutions while capping total orbits on large viewports.
 */
export function getNebulabrotSampleGrid(canvasWidth, canvasHeight) {
  const targetW = Math.max(1, canvasWidth * 2);
  const targetH = Math.max(1, canvasHeight * 2);
  const total = targetW * targetH;
  const scale = total > NEBULABROT_MAX_SAMPLES
    ? Math.sqrt(NEBULABROT_MAX_SAMPLES / total)
    : 1;
  return {
    samplesX: Math.max(canvasWidth, Math.round(targetW * scale)),
    samplesY: Math.max(canvasHeight, Math.round(targetH * scale)),
  };
}

/**
 * Accumulate Nebulabrot orbit visit counts across a horizontal slice of the sample grid.
 * Returns a Float32Array of length `canvasWidth * canvasHeight * 4` storing 4 planes:
 * [rHist, gHist, bHist, shipHist].
 */
export function accumulateNebulabrotHistogram({
  canvasWidth,
  canvasHeight,
  xRange,
  yRange,
  samplesX: rawSamplesX,
  samplesY: rawSamplesY,
  startSampleY = 0,
  sampleRegionHeight,
  simdKernel = null,
  motionPreview = false,
}) {
  const pixelCount = canvasWidth * canvasHeight;
  const histBuffer = new Float32Array(pixelCount * 4);
  if (!(canvasWidth > 0 && canvasHeight > 0)) return histBuffer;

  const grid = rawSamplesX && rawSamplesY
    ? { samplesX: rawSamplesX, samplesY: rawSamplesY }
    : getNebulabrotSampleGrid(canvasWidth, canvasHeight);
  const { samplesX, samplesY } = grid;
  const endSampleY = Math.min(
    samplesY,
    startSampleY + (sampleRegionHeight ?? samplesY),
  );
  if (startSampleY >= endSampleY) return histBuffer;

  const rHist = histBuffer.subarray(0, pixelCount);
  const gHist = histBuffer.subarray(pixelCount, pixelCount * 2);
  const bHist = histBuffer.subarray(pixelCount * 2, pixelCount * 3);
  const shipHist = histBuffer.subarray(pixelCount * 3, pixelCount * 4);

  const xMin = xRange[0];
  const xMax = xRange[1];
  const yMin = yRange[0];
  const yMax = yRange[1];
  const xSpan = xMax - xMin;
  const ySpan = yMax - yMin;
  if (!(xSpan > 0 && ySpan > 0)) return histBuffer;
  const hasTopOverscan = xMin >= 0 && yMin < 0;
  const ySampleMin = hasTopOverscan ? yMin - 0.22 * ySpan : yMin;
  const ySampleSpan = yMax - ySampleMin;

  const scaleX = canvasWidth / xSpan;
  const scaleY = canvasHeight / ySpan;
  const biasX = -xMin * scaleX - 0.5;
  const biasY = -yMin * scaleY - 0.5;
  const xInnerMin = xMin + 0.5 / scaleX;
  const xInnerMax = xMax - 1.5 / scaleX;
  const yInnerMin = yMin + 0.5 / scaleY;
  const yInnerMax = yMax - 1.5 / scaleY;
  const maxCornerX2 = Math.max(xInnerMin * xInnerMin, xInnerMax * xInnerMax);
  const maxCornerY2 = Math.max(yInnerMin * yInnerMin, yInnerMax * yInnerMax);
  const viewInsideDisk = maxCornerX2 + maxCornerY2 <= ESCAPE_THRESHOLD;
  const midX = (xMin + xMax) * 0.5;
  const midY = (yMin + yMax) * 0.5;
  const useExactCycles = Math.abs(midX) <= 0.5 && Math.abs(midY) <= 0.55;
  const rowStride = motionPreview && (endSampleY - startSampleY >= 4) ? 2 : 1;
  const hitWeight = rowStride;
  const xStep = xSpan / samplesX;
  const yStep = ySampleSpan / samplesY;
  const sampleAreaWeight = ((canvasWidth * canvasHeight) / (samplesX * samplesY)) * rowStride;

  const scatterOrbit = (x0, y0, iter, weight, canScatterY, needsYMinCheck) => {
    let x = x0;
    let y = y0;
    const kLimit = viewInsideDisk ? iter - 1 : iter;
    const primaryHist = iter <= 48 ? rHist : (iter <= 104 ? gHist : bHist);
    const secondaryHist = (iter >= 34 && iter <= 48)
      ? gHist
      : ((iter >= 68 && iter <= 104) ? bHist : null);
    for (let k = 1; k < kLimit; k++) {
      const absXY = Math.abs(x * y);
      const xn = x * x - y * y + x0;
      const yn = absXY + absXY + y0;
      x = xn;
      y = yn;
      if (x < xInnerMax && y < yInnerMax && x > xInnerMin && (!needsYMinCheck || y > yInnerMin)) {
        const fx = x * scaleX + biasX;
        const fy = y * scaleY + biasY;
        const ix = fx | 0;
        const iy = fy | 0;
        const tx = fx - ix;
        const ty = fy - iy;
        const omtX = (1 - tx) * weight;
        const txW = tx * weight;
        const omtY = 1 - ty;
        const w00 = omtX * omtY;
        const w10 = txW * omtY;
        const w01 = omtX * ty;
        const w11 = txW * ty;
        const idx = iy * canvasWidth + ix;
        primaryHist[idx] += w00;
        primaryHist[idx + 1] += w10;
        primaryHist[idx + canvasWidth] += w01;
        primaryHist[idx + canvasWidth + 1] += w11;
        if (secondaryHist !== null) {
          secondaryHist[idx] += w00;
          secondaryHist[idx + 1] += w10;
          secondaryHist[idx + canvasWidth] += w01;
          secondaryHist[idx + canvasWidth + 1] += w11;
        }
      }
    }
  };

  const addShipSample = (x0, y0, iter, distSq, weight) => {
    if (x0 >= -1.36) return;
    const fy = (y0 - yMin) * scaleY - 0.5;
    if (fy < -0.5 || fy >= canvasHeight - 0.5) return;
    let smoothIter = iter;
    if (distSq > 4.0) {
      const logZn = 0.5 * Math.log(distSq);
      const nu = Math.log(logZn * LOG2_INV) * LOG2_INV;
      if (Number.isFinite(nu)) smoothIter = iter + 1 - nu;
    }
    const rawVal = smoothIter - 17;
    if (rawVal <= 0 || smoothIter >= 120) return;
    const rise = 1 - Math.exp(-rawVal * 0.095);
    const t = smoothIter > 50 ? (120 - smoothIter) / 70 : 1;
    const taper = t * t;
    const xFade = x0 <= -1.50 ? 1 : (-1.36 - x0) / 0.14;
    const shipVal = 36 * rise * taper * (xFade * xFade) * weight;
    const fx = (x0 - xMin) * scaleX - 0.5;
    const ix = Math.max(0, Math.min(canvasWidth - 2, fx | 0));
    const iy = Math.max(0, Math.min(canvasHeight - 2, fy | 0));
    const tx = Math.max(0, Math.min(1, fx - ix));
    const ty = Math.max(0, Math.min(1, fy - iy));
    const omtX = 1 - tx;
    const omtY = 1 - ty;
    const idx = iy * canvasWidth + ix;
    shipHist[idx] += shipVal * omtX * omtY;
    shipHist[idx + 1] += shipVal * tx * omtY;
    shipHist[idx + canvasWidth] += shipVal * omtX * ty;
    shipHist[idx + canvasWidth + 1] += shipVal * tx * ty;
  };

  const kernelCap = simdKernel ? (simdKernel.maxCapacity || simdKernel.capacity || 0) : 0;
  if (simdKernel && kernelCap > 0) {
    const rowIters = new Uint32Array(samplesX);
    const rowDist = new Float64Array(samplesX);
    const activeCols = new Int32Array(samplesX);
    const subKeep = new Uint8Array(kernelCap);
    const enableSubSampling = !motionPreview && canvasWidth >= 16 && canvasHeight >= 16;

    for (let i = startSampleY; i < endSampleY; i += rowStride) {
      const y0 = ySampleMin + (i + 0.5) * yStep;
      const canScatterY = y0 < yInnerMax;
      const needsYMinCheck = !(y0 > yInnerMin);

      for (let colStart = 0; colStart < samplesX; colStart += kernelCap) {
        const count = Math.min(kernelCap, samplesX - colStart);
        for (let k = 0; k < count; k++) {
          simdKernel.cx[k] = xMin + (colStart + k + 0.5) * xStep;
        }
        simdKernel.cy.fill(y0, 0, count);
        simdKernel.iterate(count, MAX_ITERATIONS, useExactCycles);
        for (let k = 0; k < count; k++) {
          rowIters[colStart + k] = simdKernel.iterations[k];
          rowDist[colStart + k] = simdKernel.distances[k];
        }
      }

      if (!enableSubSampling) {
        for (let j = 0; j < samplesX; j++) {
          const iter = rowIters[j];
          if (iter === MAX_ITERATIONS || iter < 18) continue;
          const x0 = xMin + (j + 0.5) * xStep;
          addShipSample(x0, y0, iter, rowDist[j], sampleAreaWeight);
          if (iter >= 20 && canScatterY) {
            scatterOrbit(x0, y0, iter, hitWeight, canScatterY, needsYMinCheck);
          }
        }
        continue;
      }

      let activeCount = 0;
      for (let j = 0; j < samplesX; j++) {
        const it = rowIters[j];
        if (it >= 18 && it < MAX_ITERATIONS) {
          activeCols[activeCount++] = j;
        }
      }
      if (activeCount === 0) continue;

      const activeRatio = activeCount / samplesX;
      const subDiv = activeRatio < 0.16 ? 3 : 2;
      const subCount = subDiv * subDiv;
      const shipWeight = sampleAreaWeight / subCount;
      const sparseScatter = subDiv === 2 && activeRatio >= 0.24;
      const orbitWeight = sparseScatter ? (2 * hitWeight) / subCount : hitWeight / subCount;
      const invSub = 1 / subDiv;

      let batchLen = 0;
      const flushBatch = () => {
        if (batchLen === 0) return;
        simdKernel.iterate(batchLen, MAX_ITERATIONS, useExactCycles);
        for (let k = 0; k < batchLen; k++) {
          const iter = simdKernel.iterations[k];
          if (iter === MAX_ITERATIONS || iter < 18) continue;
          const sx0 = simdKernel.cx[k];
          const sy0 = simdKernel.cy[k];
          addShipSample(sx0, sy0, iter, simdKernel.distances[k], shipWeight);
          if (iter >= 20 && canScatterY && subKeep[k]) {
            scatterOrbit(sx0, sy0, iter, orbitWeight, canScatterY, needsYMinCheck);
          }
        }
        batchLen = 0;
      };

      for (let a = 0; a < activeCount; a++) {
        const j = activeCols[a];
        const cellXMin = xMin + j * xStep;
        const cellYMin = ySampleMin + i * yStep;
        for (let sy = 0; sy < subDiv; sy++) {
          const sy0 = cellYMin + (sy + 0.5) * invSub * yStep;
          for (let sx = 0; sx < subDiv; sx++) {
            simdKernel.cx[batchLen] = cellXMin + (sx + 0.5) * invSub * xStep;
            simdKernel.cy[batchLen] = sy0;
            subKeep[batchLen] = (!sparseScatter || ((sx + sy) & 1) === 0) ? 1 : 0;
            batchLen++;
            if (batchLen === kernelCap) flushBatch();
          }
        }
      }
      flushBatch();
    }
    return histBuffer;
  }

  const hitX = new Float64Array(MAX_ITERATIONS);
  const hitY = new Float64Array(MAX_ITERATIONS);

  for (let i = startSampleY; i < endSampleY; i += rowStride) {
    const y0 = ySampleMin + (i + 0.5) * ySampleSpan / samplesY;
    const canScatterY = y0 < yInnerMax;
    const needsYMinCheck = !(y0 > yInnerMin);
    for (let j = 0; j < samplesX; j++) {
      const x0 = xMin + (j + 0.5) * xSpan / samplesX;
      if (x0 * x0 + y0 * y0 <= 0.0624995
        || (x0 >= -1.0189 && x0 <= -0.9811 && y0 >= -0.0189 && y0 <= 0.0189)) {
        continue;
      }
      // First step from (0, 0) lands at z1 = (x0, y0)
      let x = x0;
      let y = y0;
      let x2 = x * x;
      let y2 = y * y;
      let iter = 1;
      let ox = NaN;
      let oy = NaN;
      let checkAt = 8;
      let step = 0;

      while (x2 + y2 < ESCAPE_THRESHOLD && iter < MAX_ITERATIONS) {
        const absXY = Math.abs(x * y);
        x = x2 - y2 + x0;
        y = absXY + absXY + y0;
        x2 = x * x;
        y2 = y * y;
        hitX[iter] = x;
        hitY[iter] = y;
        iter++;
        if (x === ox && y === oy) {
          iter = MAX_ITERATIONS;
          break;
        }
        if (++step === checkAt) {
          ox = x;
          oy = y;
          step = 0;
          checkAt <<= 1;
        }
      }

      if (iter === MAX_ITERATIONS || iter < 18) continue;

      addShipSample(x0, y0, iter, x2 + y2, sampleAreaWeight);

      if (iter < 20 || !canScatterY) continue;
      const kLimit = viewInsideDisk ? iter - 1 : iter;
      const primaryHist = iter <= 48 ? rHist : (iter <= 104 ? gHist : bHist);
      const secondaryHist = (iter >= 34 && iter <= 48)
        ? gHist
        : ((iter >= 68 && iter <= 104) ? bHist : null);
      for (let k = 1; k < kLimit; k++) {
        const xk = hitX[k];
        if (xk >= xInnerMax || xk <= xInnerMin) continue;
        const yk = hitY[k];
        if (yk >= yInnerMax || (needsYMinCheck && yk <= yInnerMin)) continue;
        const fx = xk * scaleX + biasX;
        const fy = yk * scaleY + biasY;
        const ix = fx | 0;
        const iy = fy | 0;
        const tx = fx - ix;
        const ty = fy - iy;
        const omtX = (1 - tx) * hitWeight;
        const txW = tx * hitWeight;
        const omtY = 1 - ty;
        const w00 = omtX * omtY;
        const w10 = txW * omtY;
        const w01 = omtX * ty;
        const w11 = txW * ty;
        const idx = iy * canvasWidth + ix;
        primaryHist[idx] += w00;
        primaryHist[idx + 1] += w10;
        primaryHist[idx + canvasWidth] += w01;
        primaryHist[idx + canvasWidth + 1] += w11;
        if (secondaryHist !== null) {
          secondaryHist[idx] += w00;
          secondaryHist[idx + 1] += w10;
          secondaryHist[idx + canvasWidth] += w01;
          secondaryHist[idx + canvasWidth + 1] += w11;
        }
      }
    }
  }

  return histBuffer;
}

function boxBlurPass(src, dst, width, height, minRow, maxRow, radius, tmp) {
  const xMinBound = 1;
  const xMaxBound = Math.max(1, width - 3);
  const hm1 = Math.max(1, height - 3);
  const yStart = Math.max(1, minRow - radius);
  const yEnd = Math.min(hm1, maxRow + radius);
  const invLen = 1 / (2 * radius + 1);

  for (let y = yStart; y <= yEnd; y++) {
    const row = y * width;
    let sum = src[row + xMinBound] * (radius + 1);
    for (let k = 1; k <= radius; k++) {
      const rx = xMinBound + k <= xMaxBound ? xMinBound + k : xMaxBound;
      sum += src[row + rx];
    }
    for (let x = xMinBound; x <= xMaxBound; x++) {
      tmp[row + x] = sum * invLen;
      const outX = x - radius >= xMinBound ? x - radius : xMinBound;
      const inX = x + radius + 1 <= xMaxBound ? x + radius + 1 : xMaxBound;
      sum += src[row + inX] - src[row + outX];
    }
    tmp[row] = tmp[row + xMinBound];
    if (width >= 2) tmp[row + width - 2] = tmp[row + xMaxBound];
    if (width >= 1) tmp[row + width - 1] = tmp[row + xMaxBound];
  }

  for (let x = 0; x < width; x++) {
    let sum = tmp[yStart * width + x] * (radius + 1);
    for (let k = 1; k <= radius; k++) {
      const ry = yStart + k <= yEnd ? yStart + k : yEnd;
      sum += tmp[ry * width + x];
    }
    for (let y = yStart; y <= yEnd; y++) {
      if (y >= minRow && y <= maxRow) {
        dst[y * width + x] = sum * invLen;
      }
      const outY = y - radius >= yStart ? y - radius : yStart;
      const inY = y + radius + 1 <= yEnd ? y + radius + 1 : yEnd;
      sum += tmp[inY * width + x] - tmp[outY * width + x];
    }
  }
}

function padFilteredBorders(buf, width, height) {
  if (width < 4 || height < 4) return;
  const xMaxValid = width - 3;
  const yMaxValid = height - 3;
  for (let y = 1; y <= yMaxValid; y++) {
    const row = y * width;
    buf[row] = buf[row + 1];
    buf[row + width - 2] = buf[row + xMaxValid];
    buf[row + width - 1] = buf[row + xMaxValid];
  }
  buf.set(buf.subarray(width, width * 2), 0);
  buf.set(buf.subarray(yMaxValid * width, (yMaxValid + 1) * width), (height - 2) * width);
  buf.set(buf.subarray(yMaxValid * width, (yMaxValid + 1) * width), (height - 1) * width);
}

function despikeShipHistogramInPlace(shipHist, bHist, width, height, sMax, bMax, minRow, maxRow, tmp, smoothBuf) {
  if (!(sMax > 0) || minRow > maxRow || width < 7 || height < 7) return;
  const wm2 = width - 2;
  const hm2 = height - 2;
  const yStart = Math.max(2, minRow);
  const yEnd = Math.min(hm2 - 1, maxRow);
  const w2 = width * 2;

  tmp.set(shipHist);
  for (let y = yStart; y <= yEnd; y++) {
    const row = y * width;
    for (let x = 2; x < wm2; x++) {
      const idx = row + x;
      const v = tmp[idx];
      if (v <= 0) continue;
      const l1 = tmp[idx - 1], r1 = tmp[idx + 1];
      const u1 = tmp[idx - width], d1 = tmp[idx + width];
      const ul1 = tmp[idx - width - 1], dr1 = tmp[idx + width + 1];
      const ur1 = tmp[idx - width + 1], dl1 = tmp[idx + width - 1];
      const sup1 = Math.max(
        l1 < r1 ? l1 : r1,
        u1 < d1 ? u1 : d1,
        ul1 < dr1 ? ul1 : dr1,
        ur1 < dl1 ? ur1 : dl1,
      );
      const l2 = tmp[idx - 2], r2 = tmp[idx + 2];
      const u2 = tmp[idx - w2], d2 = tmp[idx + w2];
      const ul2 = tmp[idx - w2 - 2], dr2 = tmp[idx + w2 + 2];
      const ur2 = tmp[idx - w2 + 2], dl2 = tmp[idx - w2 - 2];
      const max2 = Math.max(l2, r2, u2, d2, ul2, dr2, ur2, dl2);
      if (max2 === 0 && sup1 < v * 0.25) {
        shipHist[idx] = sup1 * 0.5;
      } else if (v > sup1 * 1.6 + sMax * 0.04) {
        shipHist[idx] = sup1 * 1.25 + (v - sup1 * 1.25) * 0.25;
      }
    }
  }

  if (bMax > 0) {
    boxBlurPass(shipHist, smoothBuf, width, height, minRow, maxRow, 2, tmp);
    boxBlurPass(smoothBuf, smoothBuf, width, height, minRow, maxRow, 2, tmp);
    const invB = 1 / (bMax * 0.18);
    for (let y = minRow; y <= maxRow; y++) {
      const row = y * width;
      for (let x = 0; x < width; x++) {
        const idx = row + x;
        const bv = bHist[idx];
        if (bv <= 0) continue;
        const sharp = shipHist[idx];
        const smooth = smoothBuf[idx];
        if (sharp === 0 && smooth === 0) continue;
        const tb = Math.min(1, bv * invB);
        const wSmooth = tb * tb;
        shipHist[idx] = (sharp + (smooth - sharp) * wSmooth) * (1 - 0.35 * wSmooth);
      }
    }
  }
  padFilteredBorders(shipHist, width, height);
}

function atrousBilateralPass(src, dst, width, height, minRow, maxRow, step, invSigmaSq, maxRef) {
  const xMinBound = 1;
  const xMaxBound = Math.max(1, width - 3);
  const yMinBound = 1;
  const yMaxBound = Math.max(1, height - 3);
  const yStart = Math.max(yMinBound, minRow);
  const yEnd = Math.min(yMaxBound, maxRow);
  const sw = step * width;
  const noiseFloor = maxRef * 0.025;

  for (let y = yStart; y <= yEnd; y++) {
    const row = y * width;
    const yn = y - step >= yMinBound ? row - sw : yMinBound * width;
    const yp = y + step <= yMaxBound ? row + sw : yMaxBound * width;
    for (let x = xMinBound; x <= xMaxBound; x++) {
      const idx = row + x;
      const c = src[idx];
      const xn = x - step >= xMinBound ? x - step : xMinBound;
      const xp = x + step <= xMaxBound ? x + step : xMaxBound;

      const v00 = src[yn + xn], v01 = src[yn + x], v02 = src[yn + xp];
      const v10 = src[row + xn],                    v12 = src[row + xp];
      const v20 = src[yp + xn], v21 = src[yp + x], v22 = src[yp + xp];

      if (c + v00 + v01 + v02 + v10 + v12 + v20 + v21 + v22 === 0) {
        dst[idx] = 0;
        continue;
      }

      const localScale = (c + noiseFloor) / invSigmaSq;

      const d01 = v01 - c; const w01 = 2 / (1 + (d01 * d01) / localScale);
      const d10 = v10 - c; const w10 = 2 / (1 + (d10 * d10) / localScale);
      const d12 = v12 - c; const w12 = 2 / (1 + (d12 * d12) / localScale);
      const d21 = v21 - c; const w21 = 2 / (1 + (d21 * d21) / localScale);

      const d00 = v00 - c; const w00 = 1 / (1 + (d00 * d00) / localScale);
      const d02 = v02 - c; const w02 = 1 / (1 + (d02 * d02) / localScale);
      const d20 = v20 - c; const w20 = 1 / (1 + (d20 * d20) / localScale);
      const d22 = v22 - c; const w22 = 1 / (1 + (d22 * d22) / localScale);

      const wSum = 4 + w01 + w10 + w12 + w21 + w00 + w02 + w20 + w22;
      dst[idx] = (
        4 * c +
        w01 * v01 + w10 * v10 + w12 * v12 + w21 * v21 +
        w00 * v00 + w02 * v02 + w20 * v20 + w22 * v22
      ) / wSum;
    }
  }
  padFilteredBorders(dst, width, height);
}

function adaptiveSmoothChannelInPlace(hist, width, height, maxRef, minRow, maxRow, tmp, smoothBuf) {
  if (!(maxRef > 0) || minRow > maxRow || width < 3 || height < 3) return;
  if (width < 7 || height < 7) {
    const wm1 = width - 1;
    const hm1 = height - 1;
    const yStart = minRow > 0 ? minRow - 1 : 0;
    const yEnd = maxRow < hm1 ? maxRow + 1 : hm1;
    for (let y = yStart; y <= yEnd; y++) {
      const row = y * width;
      tmp[row] = (3 * hist[row] + hist[row + 1]) * 0.25;
      for (let x = 1; x < wm1; x++) {
        const idx = row + x;
        tmp[idx] = (hist[idx - 1] + 2 * hist[idx] + hist[idx + 1]) * 0.25;
      }
      tmp[row + wm1] = (hist[row + wm1 - 1] + 3 * hist[row + wm1]) * 0.25;
    }
    const knee = maxRef * 0.22;
    const invKnee = 1 / knee;
    for (let y = minRow; y <= maxRow; y++) {
      const ym1 = (y > 0 ? y - 1 : 0) * width;
      const y0 = y * width;
      const yp1 = (y < hm1 ? y + 1 : hm1) * width;
      for (let x = 0; x < width; x++) {
        const idx = y0 + x;
        const sharp = hist[idx];
        if (sharp >= knee) continue;
        const mid = tmp[idx];
        const v1 = tmp[ym1 + x] + tmp[yp1 + x];
        if (mid === 0 && v1 === 0) continue;
        const smooth = (v1 + 2 * mid) * 0.25;
        const m = sharp > smooth ? sharp : smooth;
        if (m >= knee) continue;
        const t = m * invKnee;
        hist[idx] = smooth + (sharp - smooth) * (t * t);
      }
    }
    return;
  }

  const wm2 = width - 2;
  const hm2 = height - 2;
  const yStart = Math.max(2, minRow);
  const yEnd = Math.min(hm2 - 1, maxRow);
  const w2 = width * 2;

  padFilteredBorders(hist, width, height);

  for (let pass = 0; pass < 2; pass++) {
    const src = pass === 0 ? hist : tmp;
    const dst = pass === 0 ? tmp : hist;
    dst.set(src);
    for (let y = yStart; y <= yEnd; y++) {
      const row = y * width;
      for (let x = 2; x < wm2; x++) {
        const idx = row + x;
        const v = src[idx];
        if (v <= 0) continue;
        const l1 = src[idx - 1], r1 = src[idx + 1];
        const u1 = src[idx - width], d1 = src[idx + width];
        const ul1 = src[idx - width - 1], dr1 = src[idx + width + 1];
        const ur1 = src[idx - width + 1], dl1 = src[idx + width - 1];
        const sup1 = Math.max(
          l1 < r1 ? l1 : r1,
          u1 < d1 ? u1 : d1,
          ul1 < dr1 ? ul1 : dr1,
          ur1 < dl1 ? ur1 : dl1,
        );
        const l2 = src[idx - 2], r2 = src[idx + 2];
        const u2 = src[idx - w2], d2 = src[idx + w2];
        const ul2 = src[idx - w2 - 2], dr2 = src[idx + w2 + 2];
        const ur2 = src[idx - w2 + 2], dl2 = src[idx - w2 - 2];
        const sup2 = Math.max(
          l2 < r2 ? l2 : r2,
          u2 < d2 ? u2 : d2,
          ul2 < dr2 ? ul2 : dr2,
          ur2 < dl2 ? ur2 : dl2,
        );
        const nMean = (l1 + r1 + u1 + d1 + ul1 + ur1 + dl1 + dr1) * 0.125;
        const ridge = sup1 < sup2 * 1.2 ? sup1 : sup2 * 1.2;
        const support = ridge > nMean ? ridge : nMean;
        if (v > support * 1.18 + maxRef * 0.002) {
          dst[idx] = support * 1.04 + (v - support * 1.04) * 0.08;
        }
      }
    }
    padFilteredBorders(dst, width, height);
  }

  const invSigmaSq = 1 / (maxRef * 0.085);
  atrousBilateralPass(hist, tmp, width, height, 1, height - 3, 1, invSigmaSq, maxRef);
  atrousBilateralPass(tmp, smoothBuf, width, height, 1, height - 3, 2, invSigmaSq, maxRef);
  atrousBilateralPass(smoothBuf, hist, width, height, 1, height - 3, 3, invSigmaSq * 1.2, maxRef);
}

function fastPositiveQuantileAndBounds(arr, width, height, q, binsScratch) {
  let maxVal = 0;
  let nonZeroCount = 0;
  let firstIdx = -1;
  let lastIdx = -1;
  const len = arr.length;
  const step = len > 90000 ? 4 : 1;
  for (let i = 0; i < len; i += step) {
    const v = arr[i];
    if (v > 0) {
      if (firstIdx < 0) firstIdx = i;
      lastIdx = i;
      if (v > maxVal) maxVal = v;
    }
  }
  if (firstIdx < 0 || !(maxVal > 0)) {
    return { quantile: 1, minRow: height, maxRow: -1 };
  }
  const dustFloor = maxVal * 0.0015;
  const minRow = Math.max(0, ((firstIdx / width) | 0) - 2);
  const maxRow = Math.min(height - 1, ((lastIdx / width) | 0) + 2);
  binsScratch.fill(0);
  const invMax = 1 / maxVal;
  const maxBin = NEBULABROT_QUANTILE_BINS - 1;
  for (let i = firstIdx; i <= lastIdx; i += step) {
    const v = arr[i];
    if (v > dustFloor) {
      nonZeroCount++;
      binsScratch[(Math.sqrt(v * invMax) * maxBin) | 0]++;
    }
  }
  if (nonZeroCount === 0) {
    return { quantile: 1, minRow: height, maxRow: -1 };
  }
  const targetFromTop = Math.max(1, Math.ceil(nonZeroCount * (1 - q)));
  let acc = 0;
  let quantile = maxVal;
  for (let b = maxBin; b >= 0; b--) {
    acc += binsScratch[b];
    if (acc >= targetFromTop) {
      const u = (b + 0.5) / NEBULABROT_QUANTILE_BINS;
      quantile = Math.max(1e-6, u * u * maxVal);
      break;
    }
  }
  return { quantile, minRow, maxRow };
}

/**
 * Convert a 4-plane Nebulabrot histogram buffer into an RGBA Uint8ClampedArray.
 */
export function finalizeNebulabrotPixels(histBuffer, canvasWidth, canvasHeight) {
  const pixelCount = canvasWidth * canvasHeight;
  const rgba = new Uint8ClampedArray(pixelCount * 4);
  if (!(pixelCount > 0) || histBuffer.length < pixelCount * 4) return rgba;
  const rgba32 = new Uint32Array(rgba.buffer);
  const { toneLut, sqrtLut } = getNebulabrotLuts();
  const { binsScratch, smoothScratch, smoothBuf } = getNebulabrotScratch(pixelCount);

  const rHist = histBuffer.subarray(0, pixelCount);
  const gHist = histBuffer.subarray(pixelCount, pixelCount * 2);
  const bHist = histBuffer.subarray(pixelCount * 2, pixelCount * 3);
  const shipHist = histBuffer.subarray(pixelCount * 3, pixelCount * 4);

  const rStats = fastPositiveQuantileAndBounds(rHist, canvasWidth, canvasHeight, 0.9985, binsScratch);
  const gStats = fastPositiveQuantileAndBounds(gHist, canvasWidth, canvasHeight, 0.9985, binsScratch);
  const bStats = fastPositiveQuantileAndBounds(bHist, canvasWidth, canvasHeight, 0.9985, binsScratch);
  const sStats = fastPositiveQuantileAndBounds(shipHist, canvasWidth, canvasHeight, 0.995, binsScratch);
  const rMax = Math.max(2, rStats.quantile);
  const gMax = Math.max(2, gStats.quantile);
  const bMax = Math.max(1.5, bStats.quantile);
  const sMax = Math.max(12, sStats.quantile * 1.15);

  adaptiveSmoothChannelInPlace(rHist, canvasWidth, canvasHeight, rMax, rStats.minRow, rStats.maxRow, smoothScratch, smoothBuf);
  adaptiveSmoothChannelInPlace(gHist, canvasWidth, canvasHeight, gMax, gStats.minRow, gStats.maxRow, smoothScratch, smoothBuf);
  adaptiveSmoothChannelInPlace(bHist, canvasWidth, canvasHeight, bMax, bStats.minRow, bStats.maxRow, smoothScratch, smoothBuf);
  despikeShipHistogramInPlace(shipHist, bHist, canvasWidth, canvasHeight, sMax, bMax, sStats.minRow, sStats.maxRow, smoothScratch, smoothBuf);

  const scaleR = NEBULABROT_TONE_LUT_SIZE / rMax;
  const scaleG = NEBULABROT_TONE_LUT_SIZE / gMax;
  const scaleB = NEBULABROT_TONE_LUT_SIZE / bMax;
  const scaleS = NEBULABROT_TONE_LUT_SIZE / sMax;
  const bg32 = 0xff020b18;
  rgba32.fill(bg32);

  const minRow = Math.min(rStats.minRow, gStats.minRow, bStats.minRow, sStats.minRow);
  const maxRow = Math.max(rStats.maxRow, gStats.maxRow, bStats.maxRow, sStats.maxRow);
  if (minRow > maxRow) return rgba;
  const startPixel = Math.max(0, (minRow > 0 ? minRow - 1 : 0) * canvasWidth);
  const endPixel = Math.min(pixelCount, (maxRow + 2) * canvasWidth);

  for (let i = startPixel; i < endPixel; i++) {
    const rv = rHist[i];
    const gv = gHist[i];
    const bv = bHist[i];
    const sv = shipHist[i];
    if (rv + gv + bv + sv <= 0) continue;
    const sIdx = (sv * scaleS) | 0;
    const rIdx = (rv * scaleR) | 0;
    const gIdx = (gv * scaleG) | 0;
    const bIdx = (bv * scaleB) | 0;
    const sn = sqrtLut[sIdx > NEBULABROT_TONE_LUT_SIZE ? NEBULABROT_TONE_LUT_SIZE : sIdx];
    const tr = toneLut[rIdx > NEBULABROT_TONE_LUT_SIZE ? NEBULABROT_TONE_LUT_SIZE : rIdx];
    const tg = toneLut[gIdx > NEBULABROT_TONE_LUT_SIZE ? NEBULABROT_TONE_LUT_SIZE : gIdx];
    const tb = toneLut[bIdx > NEBULABROT_TONE_LUT_SIZE ? NEBULABROT_TONE_LUT_SIZE : bIdx];
    const rn = tr + sn * 0.85 > 1 ? 1 : tr + sn * 0.85;
    const gn = tg + sn * 0.52 > 1 ? 1 : tg + sn * 0.52;
    const bn = tb;
    const r = rn * 0.94 + gn * 0.54 + bn * 0.28;
    const g = rn * 0.18 + gn * 0.78 + bn * 0.62;
    const b = rn * 0.04 + gn * 0.18 + bn;
    const maxC = rn > gn ? (rn > bn ? rn : bn) : (gn > bn ? gn : bn);
    const a = maxC * 1.25 > 1 ? 1 : maxC * 1.25;
    const oma = 1 - a;
    const prRaw = (24 * oma + (r > 1 ? 255 : r * 255) + 0.5) | 0;
    const pgRaw = (11 * oma + (g > 1 ? 255 : g * 255) + 0.5) | 0;
    const pbRaw = (2 * oma + (b > 1 ? 255 : b * 255) + 0.5) | 0;
    const pr = prRaw > 255 ? 255 : prRaw;
    const pg = pgRaw > 255 ? 255 : pgRaw;
    const pb = pbRaw > 255 ? 255 : pbRaw;
    rgba32[i] = pr | (pg << 8) | (pb << 16) | 0xff000000;
  }

  return rgba;
}

export function renderNebulabrotRgba(params) {
  const histBuffer = accumulateNebulabrotHistogram(params);
  return finalizeNebulabrotPixels(histBuffer, params.canvasWidth, params.canvasHeight);
}

/**
 * Given a canvas element, draw a fractal using the escape time method
 * Highly optimized:
 * - Precomputed scale factors
 * - Reusable objects to avoid allocations
 * - Inlined pixel writes
 */
export function drawBurningShipFractal(canvasEl, xRange, yRange, colorFunc, colorParams) {
  const canvasWidth = canvasEl.width;
  const canvasHeight = canvasEl.height;
  if (!(canvasWidth > 0 && canvasHeight > 0)) return;
  const context = canvasEl.getContext('2d');
  if (!context) return;
  if (colorFunc === nebulabrotColor || colorFunc?.colorParams?.mode === 'nebulabrot') {
    const pixels = renderNebulabrotRgba({
      canvasWidth,
      canvasHeight,
      xRange,
      yRange,
    });
    const imageData = new ImageData(pixels, canvasWidth, canvasHeight);
    context.putImageData(imageData, 0, 0);
    return;
  }
  const canvasImageData = context.createImageData(canvasWidth, canvasHeight);
  const image = canvasImageData.data;
  const image32 = new Uint32Array(image.buffer);
  
  // Precompute scale factors to avoid division in inner loop
  const xScale = (xRange[1] - xRange[0]) / canvasWidth;
  const yScale = (yRange[1] - yRange[0]) / canvasHeight;
  renderShallowStrip(
    image, image32, canvasWidth, xRange[0], yRange[0], xScale, yScale,
    0, canvasWidth, 0, canvasHeight, colorFunc, colorParams,
  );
  context.putImageData(canvasImageData, 0, 0);
}

/**
 * Check if a range change is a pure pan (same scale, different offset)
 */
function isPanOnly(oldXRange, oldYRange, newXRange, newYRange) {
  const oldXSpan = oldXRange[1] - oldXRange[0];
  const oldYSpan = oldYRange[1] - oldYRange[0];
  const newXSpan = newXRange[1] - newXRange[0];
  const newYSpan = newYRange[1] - newYRange[0];
  
  // Check if spans are equal (within floating point tolerance)
  const tolerance = 1e-10;
  return Math.abs(oldXSpan - newXSpan) < tolerance && 
         Math.abs(oldYSpan - newYSpan) < tolerance;
}

/**
 * Apply a pan to existing canvas content and render only new edges
 * Returns true if pan was applied, false if full re-render needed
 * 
 * Optimizations:
 * - Uses Uint32Array view for bulk 4-byte pixel copying
 * - Row-based bulk copy when possible
 * - Reusable objects to avoid allocations
 */
export function applyPanAndRenderEdges(canvasEl, oldXRange, oldYRange, newXRange, newYRange, colorFunc, colorParams) {
  if (!isPanOnly(oldXRange, oldYRange, newXRange, newYRange)) {
    return false;
  }
  
  const canvasWidth = canvasEl.width;
  const canvasHeight = canvasEl.height;
  if (!(canvasWidth > 0 && canvasHeight > 0)) return false;
  const context = canvasEl.getContext('2d');
  if (!context) return false;
  
  // Calculate pixel offset
  const xScale = (newXRange[1] - newXRange[0]) / canvasWidth;
  const yScale = (newYRange[1] - newYRange[0]) / canvasHeight;
  const pixelOffsetX = Math.round((newXRange[0] - oldXRange[0]) / xScale);
  const pixelOffsetY = Math.round((newYRange[0] - oldYRange[0]) / yScale);
  
  // If offset is too large (more than 80% of canvas), just re-render
  if (Math.abs(pixelOffsetX) > canvasWidth * 0.8 || 
      Math.abs(pixelOffsetY) > canvasHeight * 0.8) {
    return false;
  }
  
  // Get existing image data
  const existingData = context.getImageData(0, 0, canvasWidth, canvasHeight);
  const newImageData = context.createImageData(canvasWidth, canvasHeight);
  const newPixels = newImageData.data;
  
  const oldPixels32 = new Uint32Array(existingData.data.buffer);
  const newPixels32 = new Uint32Array(newPixels.buffer);
  
  // Calculate valid copy range
  const srcStartY = Math.max(0, pixelOffsetY);
  const srcEndY = Math.min(canvasHeight, canvasHeight + pixelOffsetY);
  const dstStartY = Math.max(0, -pixelOffsetY);
  
  const srcStartX = Math.max(0, pixelOffsetX);
  const srcEndX = Math.min(canvasWidth, canvasWidth + pixelOffsetX);
  const dstStartX = Math.max(0, -pixelOffsetX);
  const copyWidth = srcEndX - srcStartX;
  
  // Bulk copy using Uint32Array (4 bytes per operation)
  if (copyWidth > 0) {
    for (let srcY = srcStartY, dstY = dstStartY; srcY < srcEndY; srcY++, dstY++) {
      const srcRowStart = srcY * canvasWidth + srcStartX;
      const dstRowStart = dstY * canvasWidth + dstStartX;
      // Copy entire row segment at once
      newPixels32.set(
        oldPixels32.subarray(srcRowStart, srcRowStart + copyWidth),
        dstRowStart
      );
    }
  }
  
  // Precompute scale factors
  const xBase = newXRange[0];
  const yBase = newYRange[0];
  
  // Render newly visible horizontal strip (left or right edge)
  if (pixelOffsetX !== 0) {
    const stripStartX = pixelOffsetX > 0 ? canvasWidth - pixelOffsetX : 0;
    const stripEndX = pixelOffsetX > 0 ? canvasWidth : -pixelOffsetX;
    renderShallowStrip(
      newPixels, newPixels32, canvasWidth, xBase, yBase, xScale, yScale,
      stripStartX, stripEndX, 0, canvasHeight, colorFunc, colorParams,
    );
  }
  
  // Render newly visible vertical strip (top or bottom edge)
  if (pixelOffsetY !== 0) {
    const stripStartY = pixelOffsetY > 0 ? canvasHeight - pixelOffsetY : 0;
    const stripEndY = pixelOffsetY > 0 ? canvasHeight : -pixelOffsetY;
    
    const xStart = pixelOffsetX > 0 ? 0 : (pixelOffsetX < 0 ? -pixelOffsetX : 0);
    const xEnd = pixelOffsetX > 0 ? canvasWidth - pixelOffsetX : canvasWidth;
    renderShallowStrip(
      newPixels, newPixels32, canvasWidth, xBase, yBase, xScale, yScale,
      xStart, xEnd, stripStartY, stripEndY, colorFunc, colorParams,
    );
  }
  
  context.putImageData(newImageData, 0, 0);
  return true;
}
